/**
 * SnapshotGallery — an event's frames as a picker beside one large preview.
 *
 * WHY NOT ImageCarousel (which this replaces in the event and alert records)
 * -------------------------------------------------------------------------
 * A carousel is right when there is one picture and occasionally a second. An AI event is the
 * other case: it carries the wide shot AND the crops taken out of it — full frame, person
 * crop, face crop, aligned face — and they are read TOGETHER. "What was cropped from what" is
 * the question, and a slide track answers it one frame at a time, with the others off-screen
 * behind arrows. So the frames stand in a column down the left, all visible at once, and the
 * one being read fills the panel on the right.
 *
 * THE KEYBOARD
 * ------------
 * One axis each, and neither depends on what has focus: LEFT/RIGHT move between EVENTS (the
 * RecordPager behind this modal owns those) and UP/DOWN move between this event's FRAMES.
 *
 * It was both axes on ← →, arbitrated by focus — the gallery took them once clicked into, the
 * record pager had them until then. That is invisible from the screen: the same key did one of
 * two things depending on where you last clicked, so a reader flicking through events would
 * suddenly find themselves flicking through crops instead, with nothing to say why. Separate
 * axes need no arbitration and no explaining, which is also why this claims UP/DOWN at the
 * window rather than only while focused.
 *
 * THE SMALL-CROP PROBLEM
 * ----------------------
 * A face crop is frequently 60-100px square. The carousel capped every image at its natural
 * size (`max-w-full max-h-full`), so opening one showed a postage stamp adrift in a large
 * black rectangle — the most important frame in a face event was the least readable thing in
 * the record. Here the preview SCALES A SMALL IMAGE UP to fill the panel, capped at
 * MAX_UPSCALE so a 64px crop becomes a legible 256px face rather than a blurred wall, and the
 * frame states the pixel size it actually has, so nobody reads the softness as a broken viewer.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

export interface GalleryImage {
  url: string;
  label?: string;
  /**
   * The frame's own image-type key — `fullFrame`, `personCrop2`, `vehicle_crop_0`.
   *
   * Carried so a caller can say WHICH frame to open on without knowing the order they ended up
   * in. Order is not something a caller can predict: the frames are rank-sorted here, and the
   * cloud's copy appends them as each one finishes streaming through the tunnel.
   */
  key?: string;
}

/**
 * How far a small image may be blown up in the preview.
 *
 * 4× turns a 64px face crop into 256px — the size at which a person is recognisable — and
 * stops before the point where the interpolation is all anyone can see. Anything already
 * larger than the panel is simply fitted to it; this cap only ever binds on crops.
 */
const MAX_UPSCALE = 4;

/** Natural pixel size of a loaded image, for the cap and for the caption. */
interface NaturalSize {
  w: number;
  h: number;
}

export interface SnapshotGalleryProps {
  images: GalleryImage[];
  /** Height of the preview panel — e.g. `h-[42vh]` in the event record's modal. */
  frameClassName?: string;
  /** Given, the preview becomes clickable — used to hand off to a lightbox. */
  onImageClick?: (image: GalleryImage, index: number) => void;
  /**
   * Which keys page the frames.
   *
   * `updown` (the default) leaves ← → to whatever steps through records — see THE KEYBOARD
   * above. `both` also takes ← →, for a gallery that is the only thing on screen that pages.
   * `off` binds nothing, for a caller with its own keyboard handling.
   */
  arrowKeys?: 'updown' | 'both' | 'off';
  /** A chip beside the frame's own type label — in practice, when it was captured. */
  badge?: ReactNode;
  /** Pinned bottom-right of the preview: the measurement the snapshot is evidence for. */
  overlay?: ReactNode;
  /**
   * Print the frame's pixel size in the bottom-right corner instead of in the chip row
   * top-left. For a record that names the frame and nothing else up there (the alert record),
   * where the size beside the label read as part of the frame's name. Ignored while `overlay`
   * holds that corner.
   */
  sizeCorner?: boolean;
  /**
   * Open on the frame with this `key` rather than on the first one.
   *
   * A search result is ONE PERSON out of a frame that may hold seven, and opening their record
   * on the wide shot makes the reader find them again in a crowd they have just been told
   * apart from. Their own crop is the frame the card showed and the frame the record is about.
   *
   * Applied once per KEY, as soon as a frame carrying it exists — the cloud streams its frames
   * in one at a time, so the right one is often not there on the first render. After that the
   * reader owns the selection; a later arrival must not yank the panel away from a frame they
   * chose. Changing the key re-arms it, which is what makes stepping between two people of the
   * same frame work without remounting (and, on the cloud, without re-fetching every frame).
   *
   * A key that never arrives leaves the first frame selected, which is the old behaviour.
   */
  selectKey?: string | null;
  /**
   * Bump this to select `selectKey` AGAIN after the reader has moved the gallery themselves.
   *
   * Needed because the selection is applied once per key: a reader who clicks subject #4 in the
   * breakdown, then clicks the full frame in the strip, then clicks #4 again means it the second
   * time — and without a token that second click is a no-op, because #4's key has already been
   * applied. The control looks broken exactly when someone is using it deliberately.
   *
   * Any changing value does; the callers pass a click counter.
   */
  selectToken?: number | string;
  className?: string;
  /**
   * What each frame IS ABOUT, printed in the picker beside its thumbnail.
   *
   * IN THE PICKER, not under the viewer. The subject breakdown used to be a block of its own
   * below the record: a reader looked at a crop in one place and read who it was in another, and
   * on a frame of six people matched them by a `#2` at both ends. Putting it under the big frame
   * fixed the pairing and created a new problem — the words describe ONE frame, so switching
   * frames swapped a paragraph under a picture that had not visibly changed.
   *
   * The picker is where the frames already live side by side. A rail entry that carries its own
   * subject is a list of who is in this event, each next to their own face, and choosing one is
   * the same gesture as reading about them.
   *
   * Called per image. Returning null for a frame (the wide shot is about nobody in particular)
   * leaves that entry as a plain thumbnail.
   */
  detailFor?: (image: GalleryImage, index: number) => ReactNode;
  /**
   * What each frame is about, drawn OVER its thumbnail (bottom, on a dark gradient) — and over
   * the large preview for the frame on screen.
   *
   * Preferred to `detailFor`: a caption under the thumbnail has to be clipped to fit three
   * entries into the rail, and the attributes are what got clipped. Over the picture it costs
   * no height, so the thumbnail can be tall enough to show the whole crop. Must be light-on-dark.
   */
  overlayFor?: (image: GalleryImage, index: number) => ReactNode;
  /**
   * The frame now on screen, reported back.
   *
   * The gallery owns the selection — ↑ ↓ move it, and so does a click in the picker — so a
   * caller that wants to describe what is being shown has no other way to know. The record's
   * details panel uses it to print the SELECTED subject's appearance rather than the one the
   * record happened to be opened from.
   */
  onSelectionChange?: (image: GalleryImage, index: number) => void;
}

/**
 * WHAT A FRAME'S STATE IS FILED UNDER — its own key, never its position.
 *
 * It was the index, which held only while `images` was append-only. It is not: the cloud pulls
 * the frame the record is ABOUT first (the crop the card already showed, so the record opens on
 * a picture the browser is holding rather than on a wide shot still crossing the tunnel) and
 * then files each arrival in its canonical place. Under an index, one late arrival sorted into
 * the middle would hand every frame after it somebody else's "failed", "loaded" and pixel size.
 *
 * The image type is the natural identity — `fullFrame`, `personCrop2` — and it is what the
 * caller selects by. The url is the fallback for a caller that supplies no keys, and the index
 * the last resort for one that supplies neither.
 */
const idOf = (img: GalleryImage | undefined, i: number) => img?.key || img?.url || String(i);

/**
 * How many rail entries must be WHOLE before the column starts scrolling, and the gap between
 * them (`gap-2`, kept in step with the class below).
 *
 * Three, because that is the smallest number that shows a neighbour on either side of the
 * selected entry — two only ever shows one side, and the reader cannot tell a list of two from
 * the top of a list of six.
 */
const RAIL_VISIBLE = 3;
const RAIL_GAP = 8;

/**
 * One rail tile's height: three WHOLE tiles in the rail, within a floor (a thumbnail smaller
 * than this identifies nobody) and a ceiling (a rail of two frames should not grow two posters).
 *
 * Tiles replaced the old thumbnail-plus-caption card, whose tiers traded a face against a
 * clipped caption. With the subject drawn over the picture there is only one thing to size.
 */
function tileHeight(railHeight: number, visible: number, rich: boolean): number {
  if (!rich) return 88;
  if (!railHeight || visible < 1) return 150;
  const each = (railHeight - RAIL_GAP * (visible - 1)) / visible;
  return Math.round(Math.max(104, Math.min(190, each)));
}

export default function SnapshotGallery({
  images = [],
  frameClassName,
  onImageClick,
  detailFor,
  overlayFor,
  onSelectionChange,
  arrowKeys = 'updown',
  badge,
  overlay,
  sizeCorner = false,
  selectKey,
  selectToken,
  className = '',
}: SnapshotGalleryProps) {
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState<Record<string, boolean>>({});
  const [natural, setNatural] = useState<Record<string, NaturalSize>>({});
  const touchStartX = useRef<number | null>(null);

  /**
   * ── EACH FRAME IS LOADED ONCE, AND NEVER AGAIN ──
   *
   * The viewer used to be ONE `<img>` keyed on the current url, so every change of frame
   * destroyed the element and built a new one. That costs a fetch-or-cache-hit and a decode
   * EVERY time, including on the way back to a frame that was on screen a moment ago: the panel
   * goes black, the picture reappears, and the record reads as though it is loading twice.
   *
   * On the cloud it is worse than a decode. Frames there are object URLs over blobs pulled
   * through the node tunnel, and a remount re-runs the browser's whole image pipeline for a
   * picture the page is already holding in memory.
   *
   * So a frame that has been shown STAYS MOUNTED, and selection is opacity. `shown` is which
   * ones exist in the DOM (mounted on first selection — a frame nobody opens costs nothing) and
   * `loaded` is which ones have finished decoding, which is what the shimmer waits on.
   *
   * Both are keyed by the frame's own id, like `failed` and `natural` above — see `idOf`, and
   * why a position will not do now that frames arrive out of order. Stepping to a different
   * record remounts the whole gallery (callers key it on the event id), which clears all four
   * together.
   */
  const [shown, setShown] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState<Record<string, boolean>>({});
  // Starts EMPTY rather than with the first frame: a record opened on a subject's crop (which is
  // every Search result) would otherwise mount and decode the wide shot nobody asked to see.
  useEffect(() => {
    const id = idOf(images[index], index);
    setShown((s) => (s[id] ? s : { ...s, [id]: true }));
    // `images[index]?.url` and not `images`: the array is rebuilt on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, images[index]?.url]);

  const count = images.length;
  const multiple = count > 1;

  // Clamp when the list shrinks under us (a reloaded event with fewer frames).
  useEffect(() => {
    setIndex((i) => (count === 0 ? 0 : Math.min(i, count - 1)));
  }, [count]);

  /**
   * Open on `selectKey`'s frame — once per key, as soon as that frame exists.
   *
   * The ref holds WHICH REQUEST was applied — the key plus the caller's token — not merely that
   * one was, and every part of that earns its place:
   *
   *   once per key   — the cloud streams frames in one at a time, so re-applying on every
   *                    `images` change would drag the panel back to the crop each time a later
   *                    frame arrived, overriding a reader who had moved on.
   *   per KEY        — one event now holds one crop per person, so stepping between two people
   *                    of the SAME frame changes the subject without changing the event. A
   *                    boolean would leave the second person's record open on the first
   *                    person's face, which is the exact confusion this prop exists to remove.
   *
   *   per TOKEN    — a reader who selects the same subject again, having moved the gallery in
   *                    between, means it. Without the token that click is silently a no-op.
   *
   * Two records that are different events but happen to want the same key are handled by the
   * caller's React `key`, which remounts and clears this.
   */
  const applied = useRef<string | null>(null);
  useEffect(() => {
    if (!selectKey) return;
    // The token is part of the identity, so an unchanged key with a new token re-selects. See
    // `selectToken` on why a repeat click has to count as a new instruction.
    const request = `${selectToken ?? ''}\u0000${selectKey}`;
    if (applied.current === request) return;
    const i = images.findIndex((img) => img.key === selectKey);
    if (i < 0) return;
    applied.current = request;
    setIndex(i);
  }, [images, selectKey, selectToken]);

  /**
   * ── THE SELECTION FOLLOWS THE FRAME, NOT THE POSITION ──
   *
   * `index` is a number, and the list under it is not fixed: the cloud asks for the frame the
   * record is ABOUT first and files each arrival in reading order, so a frame can appear ABOVE
   * the one on screen a second after the record opened. With the selection held as a position,
   * that insert silently changed which picture the reader was looking at — the crop they clicked
   * a result for was replaced by the wide shot, with the highlight still on "the first entry".
   *
   * So what is remembered is the frame's own id, and the position is recomputed from it whenever
   * the list changes shape. `pinned` is written only when the INDEX moves, which is the only way
   * a reader changes frames; a list that grows leaves it alone, which is what lets this find the
   * frame again afterwards.
   */
  const pinned = useRef<string | null>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { pinned.current = idOf(images[index], index); }, [index]);

  const idsKey = images.map((img, i) => idOf(img, i)).join('\u0000');
  useEffect(() => {
    const want = pinned.current;
    if (!want) return;
    const at = images.findIndex((img, i) => idOf(img, i) === want);
    if (at >= 0 && at !== index) setIndex(at);
    // Keyed on the SHAPE of the list rather than its identity: `images` is rebuilt on every
    // parent render and would re-run this for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const go = useCallback(
    (delta: number) => {
      if (count === 0) return;
      setIndex((i) => (i + delta + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (!multiple || arrowKeys === 'off') return;
    const takesSideways = arrowKeys === 'both';
    const onKey = (e: KeyboardEvent) => {
      // Never from a text field, and never from a select — a dropdown's own up/down is what
      // the reader means there.
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return;
      const back = e.key === 'ArrowUp' || (takesSideways && e.key === 'ArrowLeft');
      const fwd = e.key === 'ArrowDown' || (takesSideways && e.key === 'ArrowRight');
      if (!back && !fwd) return;
      // Up/Down would otherwise scroll the modal under the reader while the frame changes.
      e.preventDefault();
      go(back ? -1 : 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [multiple, go, arrowKeys]);

  if (count === 0) return null;

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
  };

  const current = images[index] || ({} as GalleryImage);
  const currentId = idOf(images[index], index);
  const size = natural[currentId];
  const isFailed = !!failed[currentId];

  /**
   * EVERY frame this record references is unreadable — so there is no viewer to show.
   *
   * A record can carry image keys whose files were never written or have since been pruned: the
   * appearance pass did not run, storage aged them out. Rendering the viewer anyway gives the
   * reader a 42vh black panel with "Image unavailable" in the middle of it, which looks like a
   * broken product rather than a record that has no picture — and it pushes the facts, which ARE
   * there, below the fold.
   *
   * So when nothing can be drawn the whole viewer collapses to one line. Note that this is the
   * ALL case: a record with three frames where one is missing still shows the other two, because
   * then the viewer has something to do.
   */
  const allFailed = count > 0 && images.every((img, i) => failed[idOf(img, i)]);

  /**
   * What each frame is about — see `detailFor`. Computed once for the whole rail, because the
   * rail's WIDTH depends on whether anything has detail at all: a column of bare thumbnails
   * wants 6rem, and one carrying names and attributes wants room to be read.
   */
  const details = detailFor ? images.map((img, i) => detailFor(img, i)) : [];
  const overlays = overlayFor ? images.map((img, i) => overlayFor(img, i)) : [];
  const hasDetail = details.some(Boolean) || overlays.some(Boolean);

  /**
   * THE PICKER IS ALSO THE SUBJECT LIST, so it is drawn for one frame as well as for six.
   *
   * It used to appear only where there was something to switch BETWEEN, which was right while it
   * was only a switcher. Now that each entry carries who its frame is, a single-subject event —
   * one crop, one person — rendered no rail, and with the subject described nowhere else the
   * record simply had no attributes on it. One person is exactly as worth describing as six.
   */
  const showRail = multiple || hasDetail;

  // Reported after render, not during: a caller that re-renders on this must not do so while
  // this component is still rendering.
  useEffect(() => {
    if (count > 0) onSelectionChange?.(images[index] || ({} as GalleryImage), index);
    // `images` by identity would fire on every parent render; the url of the frame is what
    // actually changes when the record does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, count, images[index]?.url]);

  /**
   * CENTRE THE SELECTED ENTRY IN THE RAIL.
   *
   * The selection moves from three places the reader is not looking at the rail from: the record
   * opens on ITS subject's crop, ↑ ↓ step through the frames, and choosing a person in the list
   * below jumps the viewer. With the entries now carrying names and attributes they are several
   * times taller than the old thumbnails, so on a six-person event the chosen one is routinely
   * off-screen — highlighted, in a part of the rail nobody can see.
   *
   * CENTRED rather than merely brought into view. "Nearest" leaves the chosen entry against the
   * top or bottom edge, which reads as the end of the list even when there are three more below
   * it; the middle is where the eye already is after clicking, and it shows what is on either
   * side of the frame being looked at.
   *
   * Scrolled by hand against the rail rather than with `scrollIntoView`, which walks up to every
   * scrollable ancestor: this lives inside a modal that scrolls, and "centre the thumbnail" must
   * never mean "scroll the record away from under the reader".
   */
  const railRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  /** False until this record's rail has been positioned once — see the animation note below. */
  const railSettled = useRef(false);

  /**
   * ── THREE ENTRIES, WHOLE, WHATEVER THE WINDOW IS ──
   *
   * An entry carrying its subject is a card — thumbnail, label, name and attributes — and at a
   * fixed size two and a bit of them filled a 42vh rail. A column whose third card is sliced
   * through the middle reads as the end of the list, so the reader does not know there is a
   * fourth; and the one useful thing a picker does — show what is on either side of the frame
   * you are looking at — is exactly what it stopped doing.
   *
   * So the entries are sized to the rail rather than the rail hoping to fit the entries: measure
   * the height, divide by three, and take the densest tier that fits. Below three frames there
   * is nothing to fit around and the roomy tier stands.
   *
   * This cannot oscillate the way a width-measuring observer can. Entry heights do not feed back
   * into the rail's height — that comes from the frame beside it — so the measurement is stable
   * on the first pass.
   */
  const [railHeight, setRailHeight] = useState(0);
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    // A 2px deadband: sub-pixel layout noise must not re-tier the column while it is being read.
    const read = () => setRailHeight((h) => (Math.abs(h - rail.clientHeight) > 2 ? rail.clientHeight : h));
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(rail);
    return () => ro.disconnect();
  }, [showRail]);

  const tileH = tileHeight(railHeight, Math.min(count, RAIL_VISIBLE), hasDetail);

  // A new record starts over: its rail has never been positioned, so the first move is a jump.
  useEffect(() => { railSettled.current = false; }, [count, images[0]?.url]);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;

    const place = (behavior: ScrollBehavior) => {
      const el = itemRefs.current[index];
      if (!el) return;

      /**
       * MEASURED WITH RECTS, NOT `offsetTop`.
       *
       * `offsetTop` is relative to the nearest POSITIONED ancestor, and `overflow-y-auto` does
       * not make an element one — so the rail was not the origin those numbers were measured
       * from. The modal's own positioned wrapper was, and its header's height went into every
       * calculation: the rail scrolled a header's worth too far and left the selected entry cut
       * off above the top edge, which is precisely the bug this was added to fix.
       *
       * A rect difference plus the current scroll is the entry's true offset inside the content,
       * whatever is or is not positioned around it.
       */
      const railBox = rail.getBoundingClientRect();
      const elBox = el.getBoundingClientRect();
      const offsetWithinContent = elBox.top - railBox.top + rail.scrollTop;

      /**
       * Centred — unless the entry is TALLER than the rail, and then aligned to its top.
       *
       * A subject with a full set of attributes can outgrow a 42vh rail on a short window.
       * Centring one of those hides its thumbnail above the fold and shows the middle of a
       * paragraph, which is the half that identifies nobody; its top shows the face and the
       * name, and the rest is one scroll away.
       */
      const target = elBox.height > rail.clientHeight
        ? offsetWithinContent
        : offsetWithinContent + elBox.height / 2 - rail.clientHeight / 2;
      const top = Math.max(0, Math.min(target, rail.scrollHeight - rail.clientHeight));

      if (Math.abs(rail.scrollTop - top) < 1) return;
      rail.scrollTo({ top, behavior });
    };

    /**
     * Instant the first time, animated afterwards.
     *
     * Opening a record already moves everything on screen; animating the rail from the top as
     * well makes the panel look like it is still loading. Once the reader is IN the record, a
     * jump is the thing that loses them — the animation is what says the list moved rather than
     * changed. Reduced-motion turns it off entirely, as it should.
     */
    const reduced = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    place(railSettled.current && !reduced ? 'smooth' : 'auto');
    railSettled.current = true;

    /**
     * AND AGAIN WHEN THE LAYOUT SETTLES.
     *
     * The first placement measures what is on screen at that instant, and the rail is not
     * finished: thumbnails are still decoding and an attribute line has not yet wrapped. Each of
     * those changes the entry heights underneath the number this just computed.
     *
     * Re-placed only while the selection is FRESH. A picture that arrives two seconds later must
     * not yank a reader who has since scrolled off to look at something else, so the observer is
     * dropped after a beat — long enough for a decode, short enough to be nobody's surprise.
     */
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => place('auto'));
    ro.observe(rail);
    for (const item of itemRefs.current) if (item) ro.observe(item);
    const stop = window.setTimeout(() => ro.disconnect(), 800);
    return () => { window.clearTimeout(stop); ro.disconnect(); };
  }, [index, showRail, count]);

  // Nothing here is focusable and it carries no ARROW_SCOPE_ATTR on purpose: that attribute
  // exists to take ← → away from a record pager, and this gallery no longer wants them — the
  // pager keeps that axis whether or not the reader has clicked a thumbnail.
  if (allFailed) {
    return (
      <div className={className}>
        <p className="rounded-lg border border-border bg-muted px-3 py-2 text-[12px] text-muted-foreground">
          {count === 1 ? 'The frame for this record' : `All ${count} frames for this record`}
          {' '}could not be loaded — they were never written, or have since been cleared from
          storage. The rest of the record is unaffected.
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* The picker is a fixed-width column and the preview takes the rest. `items-stretch` so
          the rail is exactly as tall as the frame beside it, however tall that is told to be. */}
      <div className="flex min-w-0 items-stretch gap-2.5">
        {showRail && (
          /* The column is CAPTIONED and the scroller sits inside it.
             A stack of cards with no heading is a reader's first question — the entries carry
             faces and attributes, so without a word above them it is not obvious they are the
             frames of this one event rather than other results. The caption also carries the
             count, which is the other thing the column cannot say about itself: with four
             entries and room for two, nothing on screen said there were four.

             The height moves out here and the scrolling stays on the inner box, so the caption
             does not scroll away from the list it names. */
          <div
            /* Wide enough for a person and a line of attribute chips when frames carry a
               subject; a column of bare thumbnails needs far less. */
            className={`flex flex-none flex-col ${
              hasDetail ? 'w-[12rem] sm:w-[14.5rem]' : 'w-[6.5rem] sm:w-[7.5rem]'
            } ${frameClassName || ''}`}
          >
            <div className="mb-1.5 flex shrink-0 items-center justify-between gap-1">
              <span className="truncate text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                Frames
              </span>
              <span className="shrink-0 rounded-full bg-muted px-1.5 text-[10px] font-semibold leading-4 tabular-nums text-foreground">
                {count}
              </span>
            </div>
            <div
              ref={railRef}
              /* `overflow-anchor: none`: the browser adjusts scrollTop of its own accord to keep
                 content stable when something above it changes size — a thumbnail arriving —
                 which silently moves the entry this code just centred. */
              style={{ overflowAnchor: 'none' }}
              className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-1"
            >
              {images.map((img, i) => {
                const selected = i === index;
                const over = overlays[i];
                return (
                  <button
                    key={idOf(img, i)}
                    ref={(el) => { itemRefs.current[i] = el; }}
                    type="button"
                    onClick={() => setIndex(i)}
                    title={img.label || `Frame ${i + 1}`}
                    aria-current={selected}
                    className={`group relative flex-none overflow-hidden rounded-lg bg-black text-left outline-none transition-all focus-visible:ring-2 focus-visible:ring-brand ${
                      selected
                        ? 'ring-2 ring-brand ring-offset-2 ring-offset-card'
                        : 'opacity-80 ring-1 ring-border hover:opacity-100 hover:ring-brand'
                    }`}
                  >
                    {/* THE WHOLE CROP, not a band of it. A person crop is portrait and the tile is
                        landscape, so `object-cover` showed a strip of shirt; `contain` shows the
                        person, and the same picture blurred behind fills the letterbox so the
                        tile reads as a photo rather than a black box with a figure in it. */}
                    <div className="relative w-full" style={{ height: tileH }}>
                      {failed[idOf(img, i)] ? (
                        <span className="absolute inset-0 flex items-center justify-center text-[10px] text-white/50">
                          Unavailable
                        </span>
                      ) : (
                        <>
                          <img
                            src={img.url}
                            alt=""
                            aria-hidden="true"
                            className="absolute inset-0 h-full w-full scale-110 object-cover opacity-50 blur-md"
                          />
                          <img
                            src={img.url}
                            alt=""
                            /* The thumbnail is what DISCOVERS a dead frame: every entry loads here
                               whether or not it is opened, so `allFailed` can be true of a record
                               nobody has paged through. */
                            onError={() => setFailed((f) => {
                              const id = idOf(img, i);
                              return f[id] ? f : { ...f, [id]: true };
                            })}
                            className="relative h-full w-full object-contain transition-transform duration-200 group-hover:scale-[1.03]"
                          />
                        </>
                      )}

                      {/* What the frame IS — top-left, brand when it is the one on screen, so it
                          pairs with the same chip over the large preview. */}
                      <span
                        className={`absolute left-1.5 top-1.5 flex max-w-[calc(100%-0.75rem)] items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase leading-tight tracking-wider text-white shadow ${
                          selected ? 'bg-brand' : 'bg-black/70'
                        }`}
                      >
                        {selected && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-white" />}
                        <span className="truncate">{img.label || `Frame ${i + 1}`}</span>
                      </span>

                      {/* WHO THIS FRAME IS, over the bottom of their own picture. */}
                      {over && (
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/75 to-transparent px-2 pb-1.5 pt-7">
                          {over}
                        </div>
                      )}
                    </div>

                    {/* The older caption API, for a caller that has not moved to `overlayFor`:
                        under the tile, and no longer clipped — the rail scrolls instead. */}
                    {!over && details[i] && (
                      <div className="border-t border-border bg-card px-2 py-1.5 text-left normal-case tracking-normal">
                        {details[i]}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div
          className={`relative min-w-0 flex-1 overflow-hidden rounded-xl border border-border bg-black ${frameClassName || 'aspect-video'}`}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* ONE LAYER PER FRAME THAT HAS BEEN OPENED. The selected one is opaque and the
              others are not — nothing is torn down, so returning to a frame is a change of
              opacity rather than a second load. See `shown` above for why that matters.

              Keyed by the frame's own id (see `idOf`), so a frame that arrives late and sorts
              into the middle of the list keeps its layer, its decode and its pixel size rather
              than inheriting the next frame's. */}
          {images.map((img, i) => {
            const id = idOf(img, i);
            if (!shown[id] || failed[id]) return null;
            return (
            <div
              key={id}
              aria-hidden={i !== index}
              className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${
                i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
              }`}
            >
              <img
                src={img.url}
                alt={img.label || `Snapshot ${i + 1}`}
                onLoad={(e) => {
                  const el = e.currentTarget;
                  setNatural((n) => (n[id] ? n : { ...n, [id]: { w: el.naturalWidth, h: el.naturalHeight } }));
                  setLoaded((l) => (l[id] ? l : { ...l, [id]: true }));
                }}
                onError={() => setFailed((f) => ({ ...f, [id]: true }))}
                onClick={onImageClick ? () => onImageClick(img, i) : undefined}
                /* h-full w-full + object-contain fills the panel in BOTH directions — which is
                   what scales a small crop up — while the pixel cap keeps that from becoming a
                   wall of interpolation. Before the first load there is no size to cap by, so
                   the image is simply fitted. */
                className={`h-full w-full object-contain ${onImageClick ? 'cursor-zoom-in' : ''}`}
                style={natural[id]
                  ? { maxWidth: natural[id].w * MAX_UPSCALE, maxHeight: natural[id].h * MAX_UPSCALE }
                  : undefined}
              />
            </div>
            );
          })}

          {isFailed && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[12px] text-white/50">Image unavailable</span>
            </div>
          )}

          {/* THE FIRST LOAD OF THIS FRAME, AND ONLY EVER THE FIRST. A black rectangle while a
              picture is in flight reads as a broken viewer; because the layer underneath is
              never destroyed, this cannot come back on a frame already seen. */}
          {!isFailed && !loaded[currentId] && (
            <div className="pointer-events-none absolute inset-0 animate-pulse bg-white/5" />
          )}

          {/* What this frame IS, when it was taken, and how big it really is — read together,
              so one row of chips. The pixel size is here because an upscaled crop looks soft,
              and a reader who cannot see WHY assumes the viewer is broken. */}
          <div className="pointer-events-none absolute left-3 top-3 flex flex-wrap items-center gap-1.5">
            {current.label && (
              /* BRAND, not black, when the picker is open: this chip and the highlighted entry
                 in the rail are the same claim — "this is the frame you are looking at" — and a
                 reader who cannot pair them has to work out which crop the record is showing
                 from the picture alone. Black where there is no picker to pair with. */
              <span
                className={`rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm ${
                  showRail ? 'bg-brand text-white' : 'bg-black/75 text-white'
                }`}
              >
                {current.label}
              </span>
            )}
            {badge && (
              <span className="rounded-md bg-black/75 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white/90 backdrop-blur-sm">
                {badge}
              </span>
            )}
            {size && !(sizeCorner && !overlay) && (
              <span className="rounded-md bg-black/60 px-2 py-1 text-[10px] font-semibold tabular-nums text-white/70 backdrop-blur-sm">
                {size.w} × {size.h}
              </span>
            )}
          </div>

          {size && sizeCorner && !overlay && !overlays[index] && (
            <span className="pointer-events-none absolute bottom-3 right-3 rounded-md bg-black/60 px-2 py-1 text-[11px] font-semibold tabular-nums text-white/80 backdrop-blur-sm">
              {size.w} × {size.h}
            </span>
          )}

          {/* WHERE IN THE SET THIS FRAME IS, and the two buttons that move it.
              The rail already selects a frame by pointing at it, and ↑ ↓ already page — but
              neither says how many frames there are or that paging is possible at all, so a
              reader with the wide shot on screen could not tell a one-frame record from the
              first of six. The chevrons point the way the keys do and the way the picker runs,
              so the control, the keyboard and the rail all agree on which direction is "next". */}
          {multiple && (
            <div className="absolute bottom-3 left-3 flex items-center gap-0.5 rounded-lg bg-black/70 p-0.5 backdrop-blur-sm">
              <button
                type="button"
                onClick={() => go(-1)}
                aria-label="Previous frame"
                className="flex h-6 w-6 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-white/20 hover:text-white"
              >
                <ChevronUpIcon className="h-3.5 w-3.5" />
              </button>
              <span className="px-1 text-[10px] font-semibold tabular-nums text-white/80">
                {index + 1} / {count}
              </span>
              <button
                type="button"
                onClick={() => go(1)}
                aria-label="Next frame"
                className="flex h-6 w-6 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-white/20 hover:text-white"
              >
                <ChevronDownIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {overlay && (
            <div className="pointer-events-none absolute bottom-3 right-3 rounded-lg bg-black/75 px-3 py-1.5 backdrop-blur-sm">
              {overlay}
            </div>
          )}

          {/* Who is on screen, over the large preview too — the reader looking at the big picture
              should not have to glance at the rail to know whose crop it is. Bottom-right, clear
              of the frame counter bottom-left. */}
          {!overlay && overlays[index] && !isFailed && (
            <div className="pointer-events-none absolute bottom-3 right-3 max-w-[min(20rem,60%)] rounded-lg bg-black/70 px-2.5 py-2 shadow-lg ring-1 ring-white/10 backdrop-blur-sm">
              {overlays[index]}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
